import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "./Login.css";
import "./Policy.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
// Cloudflare Turnstile site key is public by design (it ships in the page).
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAADqR-IaVCJmhWhDf";
const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 14px",
  marginTop: "6px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "8px",
  color: "#eef6ff",
  fontSize: "14px",
  fontFamily: "inherit",
};

const labelStyle = {
  display: "block",
  marginTop: "16px",
  fontSize: "13px",
  color: "rgba(210,230,255,0.75)",
  fontWeight: 600,
};

export default function SupportPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Other pages can deep-link here with the subject pre-filled (e.g. the
  // account deletion page sends "?subject=Account deletion request").
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: (searchParams.get("subject") || "").slice(0, 300),
    message: "",
    company: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");

  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Load the Turnstile script (once) and render the widget explicitly.
  useEffect(() => {
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !turnstileRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        callback: (token) => setCaptchaToken(token),
        "error-callback": () => setCaptchaToken(""),
        "expired-callback": () => setCaptchaToken(""),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`);
      if (!script) {
        script = document.createElement("script");
        script.src = TURNSTILE_SCRIPT;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", renderWidget);
      const poll = setInterval(() => {
        if (window.turnstile) {
          clearInterval(poll);
          renderWidget();
        }
      }, 300);
      setTimeout(() => clearInterval(poll), 10000);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const resetCaptcha = () => {
    setCaptchaToken("");
    if (window.turnstile && widgetIdRef.current !== null) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API_URL}/api/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, turnstileToken: captchaToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrorMsg(data.error || "Something went wrong. Please try again.");
        setStatus("error");
        resetCaptcha(); // Turnstile tokens are single-use
        return;
      }
      setStatus("sent");
      setForm({ name: "", email: "", subject: "", message: "", company: "" });
      resetCaptcha();
    } catch (err) {
      setErrorMsg("Couldn't reach the server. Please try again later.");
      setStatus("error");
      resetCaptcha();
    }
  };

  return (
    <div className="policy-page">
      <Link to="/login" className="policy-back">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        Back to Login
      </Link>

      <div className="centered-logo-container">
        <div className="logo-wrapper">
          <img src="/MP%20Full%20Color@3x.png" alt="Masters Path" className="site-logo-img" />
        </div>
      </div>

      <div className="policy-card">
        <h1>Support</h1>
        <p style={{ color: "rgba(210,230,255,0.7)", lineHeight: 1.6, marginTop: "8px" }}>
          Need help with the Masters Path Performance Dashboard? Send us a message using the form
          below and our team will get back to you by email, typically within one business day.
        </p>
        <p style={{ color: "rgba(210,230,255,0.7)", lineHeight: 1.6, marginTop: "8px" }}>
          Looking to close an account? Accounts are administered by the customer organization, so
          deletion requests are normally fulfilled through your account owner. See{" "}
          <Link to="/account-deletion" style={{ color: "#ffb57a" }}>Account and Data Deletion</Link>{" "}
          for the full process.
        </p>

        <hr className="policy-divider" />

        {status === "sent" ? (
          <div
            role="status"
            style={{
              padding: "20px",
              borderRadius: "10px",
              background: "rgba(46, 204, 113, 0.1)",
              border: "1px solid rgba(46, 204, 113, 0.35)",
              color: "#bdebcf",
            }}
          >
            <strong style={{ color: "#eafff2" }}>Thanks—your message has been sent.</strong>
            <br />
            We'll reply to the email address you provided.
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {/* Honeypot: hidden from users, bots tend to fill it */}
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={update("company")}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
            />

            <label style={labelStyle}>
              Name
              <input type="text" required value={form.name} onChange={update("name")} style={inputStyle} maxLength={200} />
            </label>

            <label style={labelStyle}>
              Email
              <input type="email" required value={form.email} onChange={update("email")} style={inputStyle} maxLength={200} />
            </label>

            <label style={labelStyle}>
              Subject
              <input type="text" value={form.subject} onChange={update("subject")} style={inputStyle} maxLength={300} />
            </label>

            <label style={labelStyle}>
              How can we help?
              <textarea required value={form.message} onChange={update("message")} style={{ ...inputStyle, minHeight: "140px", resize: "vertical" }} maxLength={5000} />
            </label>

            <div ref={turnstileRef} style={{ marginTop: "18px" }} />

            {status === "error" && (
              <div className="login-error" style={{ marginTop: "14px" }}>{errorMsg}</div>
            )}

            <button
              type="submit"
              disabled={status === "sending" || !captchaToken}
              style={{
                marginTop: "22px",
                width: "100%",
                padding: "13px",
                background: "linear-gradient(90deg,#ff7a00,#ff4a00)",
                border: "none",
                borderRadius: "10px",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 700,
                cursor: status === "sending" ? "default" : "pointer",
                opacity: status === "sending" ? 0.7 : 1,
              }}
            >
              {status === "sending" ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
